/**
 * Equipment/Rentals Booking Page - Deep-link enabled
 * Routes like /equipment/power_tools open directly to booking with preselected rental category
 */
import React from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import BookingForm from '@/components/BookingForm';
import { GlobalNav } from '@/components/GlobalNav';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { 
  Package, Wrench, Hammer, HardHat, Leaf, Sparkles, 
  Car, Dumbbell, Shield, Plug, Droplets, Flame, 
  Paintbrush, CheckCircle, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Subcategory slug to ID mapping
const SUBCATEGORY_SLUGS = {
  'power-tools': 'power_tools',
  'power_tools': 'power_tools',
  'elektritööriistad': 'power_tools',
  'hand-tools': 'hand_tools',
  'hand_tools': 'hand_tools',
  'käsitööriistad': 'hand_tools',
  'construction': 'construction',
  'ehitus': 'construction',
  'garden': 'garden',
  'aed': 'garden',
  'cleaning': 'cleaning',
  'puhastus': 'cleaning',
  'vehicles': 'vehicles',
  'sõidukid': 'vehicles',
  'lifting': 'lifting',
  'tõstmine': 'lifting',
  'safety': 'safety',
  'ohutus': 'safety',
  'electrical': 'electrical',
  'elektri': 'electrical',
  'plumbing': 'plumbing',
  'sanitaar': 'plumbing',
  'welding': 'welding',
  'keevitus': 'welding',
  'painting': 'painting',
  'värvimine': 'painting'
};

// Subcategory display info
const SUBCATEGORY_INFO = {
  'power_tools': {
    icon: Plug,
    label: { en: 'Power Tools', et: 'Elektritööriistad', fi: 'Sähkötyökalut' },
    desc: { en: 'Drills, saws, grinders and more', et: 'Puurid, saed, lihvijad jne', fi: 'Porat, sahat, hiomakoneet jne' }
  },
  'hand_tools': {
    icon: Hammer,
    label: { en: 'Hand Tools', et: 'Käsitööriistad', fi: 'Käsityökalut' },
    desc: { en: 'Hammers, wrenches, screwdrivers', et: 'Haamrid, võtmed, kruvikeerajad', fi: 'Vasarat, jakoavaimet, ruuvimeisselit' }
  },
  'construction': {
    icon: HardHat,
    label: { en: 'Construction Equipment', et: 'Ehitusseadmed', fi: 'Rakennuslaitteet' },
    desc: { en: 'Scaffolding, mixers, compactors', et: 'Tellingud, segistid, tihendajad', fi: 'Telineet, sekoittimet, tiivistimet' }
  },
  'garden': {
    icon: Leaf,
    label: { en: 'Garden Equipment', et: 'Aiaseadmed', fi: 'Puutarhalaitteet' },
    desc: { en: 'Mowers, trimmers, blowers', et: 'Muruniidukid, trimmerid, lehtpuhurid', fi: 'Ruohonleikkurit, trimmerit, puhaltimet' }
  },
  'cleaning': {
    icon: Sparkles,
    label: { en: 'Cleaning Equipment', et: 'Puhastusseadmed', fi: 'Siivouslaitteet' },
    desc: { en: 'Pressure washers, vacuums, steamers', et: 'Survepesud, tolmuimejad, aurupuhurid', fi: 'Painepesut, imurit, höyrypesurit' }
  },
  'vehicles': {
    icon: Car,
    label: { en: 'Vehicles', et: 'Sõidukid', fi: 'Ajoneuvot' },
    desc: { en: 'Vans, trailers, utility vehicles', et: 'Kaubikud, haagised, tööautod', fi: 'Pakettiautot, perävaunut, työautot' }
  },
  'lifting': {
    icon: Dumbbell,
    label: { en: 'Lifting Equipment', et: 'Tõsteseadmed', fi: 'Nostolaitteet' },
    desc: { en: 'Hoists, jacks, lifts', et: 'Tõstukid, tungrauad, tõstukid', fi: 'Nostimet, tunkit, hissit' }
  },
  'safety': {
    icon: Shield,
    label: { en: 'Safety Equipment', et: 'Ohutusvarustus', fi: 'Turvavarusteet' },
    desc: { en: 'Harnesses, helmets, safety gear', et: 'Rakmed, kiivrid, ohutusvarustus', fi: 'Valjaat, kypärät, turvavarusteet' }
  },
  'electrical': {
    icon: Plug,
    label: { en: 'Electrical Tools', et: 'Elektritööriistad', fi: 'Sähkötyökalut' },
    desc: { en: 'Cable tools, testers, electrical equipment', et: 'Kaablivahendid, testerid, elektriseadmed', fi: 'Kaapelityökalut, testerit, sähkölaitteet' }
  },
  'plumbing': {
    icon: Droplets,
    label: { en: 'Plumbing Tools', et: 'Torutööde tööriistad', fi: 'Putkityökalut' },
    desc: { en: 'Pipe cutters, wrenches, drain cleaners', et: 'Toruõilmed, võtmed, kanalisatsioonipuhastid', fi: 'Putkenleikkurit, avaimet, viemärinpuhdistimet' }
  },
  'welding': {
    icon: Flame,
    label: { en: 'Welding Equipment', et: 'Keevitusseadmed', fi: 'Hitsauslaitteet' },
    desc: { en: 'Welders, torches, protective gear', et: 'Keevitusaparaadid, põletid, kaitsevahendid', fi: 'Hitsauskoneet, polttopäät, suojavarusteet' }
  },
  'painting': {
    icon: Paintbrush,
    label: { en: 'Painting Equipment', et: 'Värvimisseadmed', fi: 'Maalauslaitteet' },
    desc: { en: 'Sprayers, rollers, scaffolding', et: 'Pihustid, rullid, tellingud', fi: 'Ruiskut, telat, telineet' }
  }
};

const EquipmentBookingPage = () => {
  const { subcategory: subcategorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const { t, language } = useLanguage();
  
  // Resolve subcategory from slug or URL param
  const subcategoryId = SUBCATEGORY_SLUGS[subcategorySlug] || searchParams.get('subcategory') || null;
  const subcategoryInfo = subcategoryId ? SUBCATEGORY_INFO[subcategoryId] : null;
  const Icon = subcategoryInfo?.icon || Package;

  // Labels for the page
  const labels = {
    en: {
      title: subcategoryInfo ? subcategoryInfo.label.en : 'Equipment Rental',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.en : 'Rent tools and equipment',
      backToRentals: 'Back to Rentals',
      features: ['Daily/weekly rates', 'Delivery available', 'Quality equipment', 'Flexible returns']
    },
    et: {
      title: subcategoryInfo ? subcategoryInfo.label.et : 'Seadmete rent',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.et : 'Rendi tööriistu ja seadmeid',
      backToRentals: 'Tagasi rendi juurde',
      features: ['Päeva/nädala hinnad', 'Kohaletoimetamine', 'Kvaliteetne varustus', 'Paindlik tagastus']
    },
    fi: {
      title: subcategoryInfo ? subcategoryInfo.label.fi : 'Laitteiden vuokraus',
      subtitle: subcategoryInfo ? subcategoryInfo.desc.fi : 'Vuokraa työkaluja ja laitteita',
      backToRentals: 'Takaisin vuokraukseen',
      features: ['Päivä/viikkohinnat', 'Toimitus saatavilla', 'Laadukkaat laitteet', 'Joustava palautus']
    },
    nb: {
      title: subcategoryInfo ? (subcategoryInfo.label.nb || subcategoryInfo.label.en) : 'Utstyrsutleie',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.nb || subcategoryInfo.desc.en) : 'Lei verktøy og utstyr',
      backToRentals: 'Tilbake til utleie',
      features: ['Dags-/ukepriser', 'Levering tilgjengelig', 'Kvalitetsutstyr', 'Fleksibel retur']
    },
    ru: {
      title: subcategoryInfo ? (subcategoryInfo.label.ru || subcategoryInfo.label.en) : 'Аренда оборудования',
      subtitle: subcategoryInfo ? (subcategoryInfo.desc.ru || subcategoryInfo.desc.en) : 'Арендуйте инструменты и оборудование',
      backToRentals: 'Назад к аренде',
      features: ['Дневные/недельные ставки', 'Доставка доступна', 'Качественное оборудование', 'Гибкий возврат']
    }
  };

  const l = labels[language] || labels.en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-slate-50">
      <GlobalNav />
      
      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Back link */}
        <Link to="/equipment" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="h-4 w-4" />
          {l.backToRentals}
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left: Info Section */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-4 bg-green-100 text-green-700">
                <Icon className="h-3.5 w-3.5 mr-1" />
                {language === 'et' ? 'Seadmete rent' : language === 'fi' ? 'Laitteiden vuokraus' : 'Equipment Rental'}
              </Badge>
              
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" data-testid="equipment-booking-title">
                {l.title}
              </h1>
              
              <p className="text-lg text-slate-600 max-w-lg" data-testid="equipment-booking-subtitle">
                {l.subtitle}
              </p>
            </motion.div>

            {/* Features */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {l.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-white rounded-full shadow-sm border text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </motion.div>

            {/* Trust Banner */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <Card className="bg-gradient-to-r from-green-600 to-green-800 text-white border-0">
                <CardContent className="py-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Shield className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">
                        {language === 'et' ? 'Kvaliteet garanteeritud' : language === 'fi' ? 'Laatu taattu' : 'Quality Guaranteed'}
                      </h3>
                      <p className="text-green-100 text-sm">
                        {language === 'et' ? 'Kõik seadmed on hooldatud ja kasutuseks valmis' : language === 'fi' ? 'Kaikki laitteet on huollettu ja käyttövalmiita' : 'All equipment is maintained and ready to use'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Right: Booking Form with preselection */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:sticky lg:top-24"
          >
            <BookingForm 
              defaultCategory="rentals"
              defaultSubcategory={subcategoryId}
            />
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default EquipmentBookingPage;
